import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  UsersIcon,
  HomeIcon,
  CalendarIcon,
  ClockIcon,
  MessageSquareIcon,
  DollarSignIcon,
  CheckCircleIcon,
  BarChart3Icon,
  TrendingUpIcon,
  FileTextIcon,
  EyeIcon,
  UserCheckIcon,
  SettingsIcon,
  GlobeIcon,
  ClipboardListIcon,
  TimerIcon,
  CalendarDaysIcon,
  ReceiptIcon,
  BuildingIcon,
  ChevronRightIcon,
} from 'lucide-react'
import {
  MobilePage,
  MobilePageHeader,
  MobileSearchField,
  MobileStatStrip,
  MobileListGroup,
  MobileGroupRow,
  MobileSection,
  mobile,
} from '../components/mobile'
import { usePermissions } from '../hooks/usePermissions'
import { permissionForPath } from '../lib/permissions'

type DirPage = {
  name: string
  description: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Explicit permission(s); falls back to ROUTE_PERMISSIONS for href. */
  permission?: string | string[]
}

type DirCategory = {
  title: string
  description: string
  color: string
  pages: DirPage[]
}

const QUICK_LINKS = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, tone: 'text-blue-600 bg-blue-50' },
  { name: 'Leads', href: '/leads', icon: UsersIcon, tone: 'text-green-600 bg-green-50' },
  { name: 'Proposals', href: '/proposals', icon: FileTextIcon, tone: 'text-purple-600 bg-purple-50' },
  { name: 'Jobs', href: '/jobs', icon: ClipboardListIcon, tone: 'text-indigo-600 bg-indigo-50' },
  { name: 'Time Clock', href: '/time-clock', icon: ClockIcon, tone: 'text-amber-600 bg-amber-50' },
  { name: 'Billing', href: '/billing', icon: DollarSignIcon, tone: 'text-orange-600 bg-orange-50' },
] as const

const PAGE_CATEGORIES: DirCategory[] = [
  {
    title: 'Core Management',
    description: 'Essential business operations',
    color: 'blue',
    pages: [
      {
        name: 'Dashboard',
        description: 'Main dashboard with overview and quick actions',
        href: '/dashboard',
        icon: HomeIcon,
      },
      {
        name: 'Leads',
        description: 'Lead management and sales pipeline tracking',
        href: '/leads',
        icon: UsersIcon,
      },
      {
        name: 'Proposals',
        description: 'Create and manage customer proposals',
        href: '/proposals',
        icon: FileTextIcon,
      },
      {
        name: 'Jobs',
        description: 'Manage field operations and job scheduling',
        href: '/jobs',
        icon: ClipboardListIcon,
      },
      {
        name: 'Job Details',
        description: 'Detailed view of specific job information',
        href: '/jobs/:id',
        icon: EyeIcon,
      },
      {
        name: 'Daily Dispatch',
        description: 'Calendar overview and daily job dispatch',
        href: '/job-calendar',
        icon: CalendarIcon,
      },
    ],
  },
  {
    title: 'Time & Attendance',
    description: 'Employee time tracking and management',
    color: 'green',
    pages: [
      {
        name: 'Time Clock',
        description: 'Clock in/out and track work hours',
        href: '/time-clock',
        icon: ClockIcon,
      },
      {
        name: 'Time Review',
        description: 'Review and manage employee time entries',
        href: '/time-review',
        icon: TimerIcon,
      },
    ],
  },
  {
    title: 'Quality Control',
    description: 'Quality assurance and inspection management',
    color: 'yellow',
    pages: [
      {
        name: 'QC Visits',
        description: 'Track QC visit results and outcomes',
        href: '/qc-visits',
        icon: CheckCircleIcon,
      },
      {
        name: 'QC Schedule',
        description: 'Schedule quality control inspections',
        href: '/qc-schedule',
        icon: CalendarDaysIcon,
      },
    ],
  },
  {
    title: 'Review & Approval',
    description: 'Job review and approval workflows',
    color: 'purple',
    pages: [
      {
        name: 'Review',
        description: 'Review completed jobs for approval',
        href: '/review',
        icon: CheckCircleIcon,
        permission: 'review.view',
      },
      {
        name: 'Review Details',
        description: 'Detailed review of specific job',
        href: '/review/:id',
        icon: EyeIcon,
        permission: 'review.view',
      },
      {
        name: 'End of Day',
        description: 'Daily operational summary and closing',
        href: '/end-of-day',
        icon: CalendarIcon,
        permission: 'review.view',
      },
    ],
  },
  {
    title: 'Financial Management',
    description: 'Invoice management and financial operations',
    color: 'indigo',
    pages: [
      {
        name: 'Billing',
        description: 'Manage invoices and billing operations',
        href: '/billing',
        icon: DollarSignIcon,
      },
      {
        name: 'Invoice Generation',
        description: 'Automated invoice generation from jobs',
        href: '/invoice-generation',
        icon: ReceiptIcon,
      },
      {
        name: 'P&L Dashboard',
        description: 'Profit and loss analysis and reporting',
        href: '/pnl-dashboard',
        icon: TrendingUpIcon,
      },
    ],
  },
  {
    title: 'Reports & Analytics',
    description: 'Business intelligence and reporting',
    color: 'orange',
    pages: [
      {
        name: 'Reports',
        description: 'Central hub for all reporting functionality',
        href: '/reports',
        icon: BarChart3Icon,
      },
    ],
  },
  {
    title: 'Customer Management',
    description: 'Customer and property management',
    color: 'teal',
    pages: [
      {
        name: 'Customers',
        description: 'Manage customer accounts and information',
        href: '/customers',
        icon: UsersIcon,
      },
      {
        name: 'Properties',
        description: 'Track property locations and details',
        href: '/properties',
        icon: BuildingIcon,
      },
      {
        name: 'Directory',
        description: 'Complete application directory (this page)',
        href: '/directory',
        icon: UserCheckIcon,
      },
    ],
  },
  {
    title: 'Communication',
    description: 'Unified messaging and communication hub',
    color: 'pink',
    pages: [
      {
        name: 'Inbox',
        description: 'WhatsApp, Email, and VOIP message management',
        href: '/messages',
        icon: MessageSquareIcon,
      },
      {
        name: 'Message Details',
        description: 'Detailed view of specific message',
        href: '/messages/:id',
        icon: MessageSquareIcon,
      },
    ],
  },
  {
    title: 'Client Portal',
    description: 'Public-facing client access',
    color: 'emerald',
    pages: [
      {
        name: 'Client Portal',
        description: 'Public client portal for job reports and photos',
        href: '/client-portal',
        icon: GlobeIcon,
      },
    ],
  },
  {
    title: 'System',
    description: 'System configuration and settings',
    color: 'gray',
    pages: [
      {
        name: 'Settings',
        description: 'System configuration and settings',
        href: '/settings',
        icon: SettingsIcon,
      },
      {
        name: 'Service Catalog',
        description: 'Manage service categories and items',
        href: '/service-catalog',
        icon: FileTextIcon,
      },
    ],
  },
]

const ICON_TONE: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-green-50 text-green-600',
  yellow: 'bg-amber-50 text-amber-600',
  purple: 'bg-purple-50 text-purple-600',
  pink: 'bg-pink-50 text-pink-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  orange: 'bg-orange-50 text-orange-600',
  teal: 'bg-teal-50 text-teal-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  gray: 'bg-gray-100 text-gray-600',
}

function pagePermission(page: DirPage): string | string[] | null {
  if (page.permission) return page.permission
  const href = page.href.includes(':') ? page.href.split('/:')[0] : page.href
  return permissionForPath(href)
}

export function Directory() {
  const navigate = useNavigate()
  const { canAny, loading: permLoading } = usePermissions()
  const [search, setSearch] = useState('')

  const visibleCategories = useMemo(() => {
    return PAGE_CATEGORIES.map((cat) => ({
      ...cat,
      pages: cat.pages.filter((p) => {
        const perm = pagePermission(p)
        if (!perm) return true
        const required = Array.isArray(perm) ? perm : [perm]
        return canAny(required)
      }),
    })).filter((cat) => cat.pages.length > 0)
  }, [canAny])

  const totalPages = visibleCategories.reduce((sum, category) => sum + category.pages.length, 0)

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return visibleCategories
    return visibleCategories
      .map((cat) => ({
        ...cat,
        pages: cat.pages.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q) ||
            p.href.toLowerCase().includes(q) ||
            cat.title.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.pages.length > 0)
  }, [search, visibleCategories])

  const quickLinks = useMemo(
    () =>
      QUICK_LINKS.filter((link) => {
        const perm = permissionForPath(link.href)
        if (!perm) return true
        const required = Array.isArray(perm) ? perm : [perm]
        return canAny(required)
      }),
    [canAny]
  )

  if (permLoading) {
    return (
      <MobilePage>
        <div className="text-gray-500 py-8 text-center">Loading...</div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader title="Directory" subtitle={`${totalPages} pages you can open`} />

      <MobileSearchField value={search} onChange={setSearch} placeholder="Search pages..." />

      <MobileStatStrip
        stats={[
          { key: 'pages', label: 'Pages', value: totalPages, tone: 'blue' },
          { key: 'cats', label: 'Categories', value: visibleCategories.length, tone: 'green' },
        ]}
      />

      {quickLinks.length > 0 && (
      <MobileSection title="Quick links">
        <div className="grid grid-cols-3 gap-2">
          {quickLinks.map((link) => {
            const Icon = link.icon
            return (
              <button
                key={link.href}
                type="button"
                onClick={() => navigate(link.href)}
                className={`${mobile.surface} flex flex-col items-center gap-1.5 p-3 min-h-[72px] active:bg-gray-50`}
              >
                <span className={`p-2 rounded-xl ${link.tone}`}>
                  <Icon className="w-5 h-5" />
                </span>
                <span className="text-[11px] font-semibold text-gray-800 text-center leading-tight">
                  {link.name}
                </span>
              </button>
            )
          })}
        </div>
      </MobileSection>
      )}

      {filteredCategories.length === 0 ? (
        <div className={mobile.emptyWrap}>
          <p className={mobile.emptyTitle}>No matching pages</p>
          <p className={mobile.emptyBody}>Try a different search term.</p>
        </div>
      ) : (
        filteredCategories.map((category) => (
          <MobileListGroup key={category.title} label={category.title}>
            {category.pages.map((page) => {
              const Icon = page.icon
              const skipDynamic = page.href.includes(':')
              return (
                <MobileGroupRow
                  key={page.href + page.name}
                  onClick={skipDynamic ? undefined : () => navigate(page.href)}
                  className={skipDynamic ? 'opacity-60' : ''}
                >
                  <div className="flex items-center gap-3 w-full min-w-0">
                    <div
                      className={`p-2 rounded-xl flex-shrink-0 ${
                        ICON_TONE[category.color] || ICON_TONE.blue
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{page.name}</p>
                      <p className="text-xs text-gray-500 truncate">{page.description}</p>
                    </div>
                    {!skipDynamic && (
                      <ChevronRightIcon className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                  </div>
                </MobileGroupRow>
              )
            })}
          </MobileListGroup>
        ))
      )}
    </MobilePage>
  )
}
